import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BatchApiAdapter } from '../adapters/BatchApiAdapter';
import type {
  ActivateProductBatchesRequest,
  BlockBatchRequest,
  CreateBatchRequest,
  UpdateBatchRequest,
} from '../models/Batch';

const useBatchAdapter = () => {
  const httpClient = createHttpClient(apiClientWH);
  return new BatchApiAdapter(httpClient);
};

export const useCreateBatch = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: (data: CreateBatchRequest) => adapter.createBatch(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['product-batches-select', variables.product_id] });
      queryClient.invalidateQueries({ queryKey: ['product-batches', variables.product_id] });
    },
  });
};

export const useUpdateBatch = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: ({ batchId, data }: { batchId: number; data: UpdateBatchRequest }) =>
      adapter.updateBatch(batchId, data),
    onSuccess: (_, { batchId }) => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
    },
  });
};

export const useDeleteBatch = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: (batchId: number) => adapter.deleteBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
  });
};

export const useBlockBatch = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: ({ batchId, data }: { batchId: number; data?: BlockBatchRequest }) => adapter.blockBatch(batchId, data),
    onSuccess: (_, { batchId }) => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
    },
  });
};

export const useUnblockBatch = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: (batchId: number) => adapter.unblockBatch(batchId),
    onSuccess: (_, batchId) => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
    },
  });
};

export const useActivateProductBatches = () => {
  const queryClient = useQueryClient();
  const adapter = useBatchAdapter();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: number; data: ActivateProductBatchesRequest }) =>
      adapter.activateProductBatches(productId, data),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['product-batches', productId] });
      queryClient.invalidateQueries({ queryKey: ['product-batches-select', productId] });
      queryClient.invalidateQueries({ queryKey: ['locations-by-product', String(productId)] });
    },
  });
};
